import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, eventShareApi, milestoneApi } from '../api/client';
import type { CarouselEvent } from '../types';
import { format } from 'date-fns';
import HTMLFlipBook from 'react-pageflip';
import { parseEventDate } from '../utils/eventDate';
import { selectSharedSlides, photoDwellMs } from '../utils/slideshowSlides';
import {
  MUSIC_TRACKS, NO_MUSIC_ID, NORMAL_MUSIC_VOLUME, DUCKED_MUSIC_VOLUME,
  resolveMusicTrack, stepVolumeToward,
} from '../utils/musicTracks';

const MUSIC_STORAGE_KEY = 'carousel_music_track_id';

type ViewMode = 'date' | 'member' | 'location' | 'milestone';
type SlideStyle = 'basic' | 'magazine' | 'photobook';

export function CarouselViewer() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startAtId = searchParams.get('startAt');
  const initialMode = (searchParams.get('mode') as ViewMode | null) ?? 'date';
  const isShared = searchParams.get('shared') === '1';
  const sharedOwner = searchParams.get('owner');
  const milestoneId = searchParams.get('milestoneId');

  const [mode, setMode] = useState<ViewMode>(initialMode);
  const [slideStyle, setSlideStyle] = useState<SlideStyle>('basic');
  const [slides, setSlides] = useState<CarouselEvent[]>([]);
  const [milestoneTitle, setMilestoneTitle] = useState('');
  // Non-milestone modes know their music choice synchronously (localStorage);
  // milestone mode has to wait for the fetch, so musicReady gates playback
  // until the real saved choice is known — avoids a flash of the wrong track.
  const [musicReady, setMusicReady] = useState(initialMode !== 'milestone');
  const [musicTrackId, setMusicTrackIdState] = useState<string | null>(() =>
    initialMode === 'milestone' ? null : localStorage.getItem(MUSIC_STORAGE_KEY)
  );
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [musicMenuOpen, setMusicMenuOpen] = useState(false);
  const musicRef = useRef<HTMLAudioElement>(null);
  const [index, setIndex] = useState(0);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [audioPlaying, setAudioPlaying] = useState<string | null>(null);
  // "Play all" / card play buttons open with &autoplay=1 — they mean "play it",
  // not "open it paused on the first photo".
  const [autoPlay, setAutoPlay] = useState(() => searchParams.get('autoplay') === '1');
  const [progressKey, setProgressKey] = useState(0);
  const audioRefs = useRef<Map<string, HTMLAudioElement>>(new Map());
  const startAtApplied = useRef(false);
  const autoPlayRef = useRef(false);
  const nextRef = useRef<() => void>(() => {});
  const autoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { autoPlayRef.current = autoPlay; }, [autoPlay]);

  function clearAutoTimer() {
    if (autoTimerRef.current) { clearTimeout(autoTimerRef.current); autoTimerRef.current = null; }
  }

  useEffect(() => {
    // Ignore a response that's no longer current: a stale one landing second
    // (React StrictMode runs this twice in dev; overlapping loads in prod)
    // used to reset the slideshow to the first event after startAt had already
    // jumped to the requested one.
    let cancelled = false;
    const cancel = () => { cancelled = true; };
    setLoading(true);
    startAtApplied.current = false;
    if (isShared) {
      eventShareApi.getSharedWithMe()
        .then(({ groups }) => {
          if (cancelled) return;
          setSlides(selectSharedSlides(groups, sharedOwner, startAtId));
          setIndex(0);
        })
        .finally(() => { if (!cancelled) setLoading(false); });
      return cancel;
    }
    if (mode === 'milestone') {
      if (!milestoneId) { setSlides([]); setLoading(false); return cancel; }
      milestoneApi.get(milestoneId)
        .then(data => {
          if (cancelled) return;
          setMilestoneTitle(data.title);
          setSlides(data.events);
          setIndex(0);
          setMusicTrackIdState(data.musicTrackId);
          setMusicReady(true);
        })
        .finally(() => { if (!cancelled) setLoading(false); });
      return cancel;
    }
    api.get(`/events?sort=${mode}&allPhotos=true`)
      .then(({ data }) => {
        if (cancelled) return;
        let events: CarouselEvent[];
        if (data.mode === 'member') {
          events = (data.groups ?? []).flatMap((g: { member: unknown; events: CarouselEvent[] }) => g.events);
        } else {
          events = data.events ?? [];
        }
        setSlides(events);
        setIndex(0);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return cancel;
  }, [mode, isShared, milestoneId, sharedOwner]); // eslint-disable-line react-hooks/exhaustive-deps -- startAtId only picks the fallback group on first load

  useEffect(() => {
    if (startAtId && slides.length > 0 && !startAtApplied.current) {
      const idx = slides.findIndex(s => s.id === startAtId);
      if (idx >= 0) {
        setIndex(idx);
        startAtApplied.current = true;
      }
    }
  }, [slides, startAtId]);

  // Whether this slide's voice story has finished (or couldn't play). Auto-play
  // leaves a slide only once every photo has been shown AND its story is done.
  const [slideAudioDone, setSlideAudioDone] = useState(false);
  // True once auto-play has sat on the last photo waiting for the story — then
  // only a short grace follows its end, not a full photo dwell on top.
  const waitedForAudioRef = useRef(false);
  // Stepping back from an event's first photo lands on the previous event's
  // LAST photo; everything else enters an event at its first.
  const enterAtPhotoRef = useRef<number | null>(null);

  useEffect(() => {
    setPhotoIndex(enterAtPhotoRef.current ?? 0);
    enterAtPhotoRef.current = null;
    setSlideAudioDone(false);
    waitedForAudioRef.current = false;
  }, [index]);

  useEffect(() => {
    audioRefs.current.forEach(audio => {
      audio.pause();
      audio.currentTime = 0;
    });
    setAudioPlaying(null);
    const firstClip = slides[index]?.audioClips?.[0];
    if (firstClip) {
      const audio = audioRefs.current.get(firstClip.id);
      if (audio) {
        // When audio finishes, auto-advance if enabled (1.5 s grace after clip ends)
        audio.onended = () => {
          setAudioPlaying(null);
          setSlideAudioDone(true);
        };
        // Blocked autoplay (iOS/WKWebView without a fresh tap) must not stall
        // auto-play forever waiting for an 'ended' that never comes.
        audio.play().then(() => setAudioPlaying(firstClip.id)).catch(() => setSlideAudioDone(true));
      }
    }
  }, [index, slides]);

  // Auto-play: every photo of an event, in order, then the next event.
  // Each photo holds photoDwellMs(); on the last photo an event with a voice
  // story waits for the story to end (1.5 s grace) before moving on.
  const currentSlide = slides[index];
  const photoCount = currentSlide?.photos.length ?? 0;
  const onLastPhoto = photoIndex >= photoCount - 1;
  const waitingForAudio = Boolean(autoPlay && currentSlide && currentSlide.audioClips.length > 0 && onLastPhoto && !slideAudioDone);
  useEffect(() => {
    clearAutoTimer();
    setProgressKey(k => k + 1);
    if (waitingForAudio) waitedForAudioRef.current = true;
    if (!autoPlay || !currentSlide || waitingForAudio) return;
    if (!onLastPhoto) {
      autoTimerRef.current = setTimeout(() => setPhotoIndex(i => i + 1), photoDwellMs(photoCount));
    } else {
      autoTimerRef.current = setTimeout(() => nextRef.current(), waitedForAudioRef.current ? 1500 : photoDwellMs(photoCount));
    }
    return clearAutoTimer;
  }, [index, photoIndex, autoPlay, currentSlide, photoCount, onLastPhoto, waitingForAudio]);

  // Background music: starts/switches when the resolved track changes. Never
  // assumes .play() succeeds — iOS/WKWebView can silently block autoplay
  // without a fresh user gesture; onPlay/onPause below (not this .catch)
  // are the single source of truth for musicPlaying, so a blocked attempt
  // just leaves the picker button showing "not playing" for the user to tap.
  useEffect(() => {
    if (!musicReady) return;
    const el = musicRef.current;
    if (!el) return;
    const track = resolveMusicTrack(musicTrackId);
    if (!track) { el.pause(); return; }
    el.loop = true;
    el.volume = NORMAL_MUSIC_VOLUME;
    el.src = track.url;
    el.play().catch(() => {});
  }, [musicTrackId, musicReady]);

  // Ducking: softens music under an actively-playing voice recording, eases
  // back up once it ends. audioPlaying already reflects "is any per-slide
  // recording playing" (set by onPlay/onPause/onEnded across every slide
  // renderer), so this is the entire mechanism — no new detection needed.
  useEffect(() => {
    const target = audioPlaying ? DUCKED_MUSIC_VOLUME : NORMAL_MUSIC_VOLUME;
    const interval = setInterval(() => {
      const el = musicRef.current;
      if (!el) return;
      const nextVolume = stepVolumeToward(el.volume, target);
      el.volume = nextVolume;
      if (nextVolume === target) clearInterval(interval);
    }, 100);
    return () => clearInterval(interval);
  }, [audioPlaying]);

  // Stop music when leaving the viewer entirely.
  useEffect(() => {
    return () => { musicRef.current?.pause(); };
  }, []);

  function selectMusic(id: string) {
    setMusicTrackIdState(id);
    setMusicMenuOpen(false);
    if (mode === 'milestone' && milestoneId) {
      milestoneApi.update(milestoneId, { musicTrackId: id }).catch(() => {});
    } else {
      localStorage.setItem(MUSIC_STORAGE_KEY, id);
    }
  }

  function toggleMusicPlayback() {
    const el = musicRef.current;
    if (!el) return;
    if (musicPlaying) el.pause();
    else el.play().catch(() => {});
  }

  const prev = useCallback(() => setIndex(i => (i - 1 + slides.length) % slides.length), [slides.length]);
  const next = useCallback(() => setIndex(i => (i + 1) % slides.length), [slides.length]);

  // Keep nextRef current so timer callbacks always use the latest version
  useEffect(() => { nextRef.current = next; }, [next]);

  // Photo-by-photo stepping (arrows, keyboard, tapping the photo): every photo
  // of an event before the next event. prev/next above stay whole-event jumps
  // for the thumbnail strip, Photo Book page turns, and auto-play's hand-off.
  const advancePhoto = useCallback(() => {
    const slide = slides[index];
    if (!slide) return;
    if (photoIndex < slide.photos.length - 1) {
      setPhotoIndex(i => i + 1);
    } else {
      next();
    }
  }, [slides, index, photoIndex, next]);

  const retreatPhoto = useCallback(() => {
    if (!slides[index]) return;
    if (photoIndex > 0) {
      setPhotoIndex(i => i - 1);
    } else {
      const prevIdx = (index - 1 + slides.length) % slides.length;
      enterAtPhotoRef.current = Math.max(0, slides[prevIdx].photos.length - 1);
      prev();
    }
  }, [slides, index, photoIndex, prev]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft') retreatPhoto();
      if (e.key === 'ArrowRight') advancePhoto();
      if (e.key === 'Escape') navigate('/events');
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [retreatPhoto, advancePhoto, navigate]);

  const current = slides[index];
  const currentPhoto = current ? (current.photos[photoIndex] ?? current.photos[0]) : null;

  function audioRef(clipId: string) {
    return (el: HTMLAudioElement | null) => {
      if (el) audioRefs.current.set(clipId, el);
      else audioRefs.current.delete(clipId);
    };
  }

  const activeMusicTrack = resolveMusicTrack(musicTrackId);

  return (
    <div className="fixed inset-0 bg-black flex flex-col z-50">
      <audio ref={musicRef} onPlay={() => setMusicPlaying(true)} onPause={() => setMusicPlaying(false)} />
      {/* Top bar */}
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-black/70 text-white backdrop-blur-sm">
        <div className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0" style={{ scrollbarWidth: 'none' }}>
          <div className="flex gap-2 flex-shrink-0 items-center min-w-0">
            {isShared ? (
              <span className="px-3 py-1 rounded-full text-xs bg-white/10 text-white/70 whitespace-nowrap">Shared with you</span>
            ) : mode === 'milestone' ? (
              <div className="min-w-0">
                <h1 className="text-sm font-serif font-bold text-white truncate">✨ {milestoneTitle}</h1>
                {slides.length > 0 && (
                  <p className="text-[0.65rem] text-stone-400 truncate">
                    {slides.reduce((n, s) => n + s.photos.length, 0)} photos · {slides.length} moments
                  </p>
                )}
              </div>
            ) : (
              (['date', 'member', 'location'] as ViewMode[]).map(m => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`px-3 py-1 rounded-full text-xs capitalize transition-colors whitespace-nowrap ${
                    mode === m ? 'bg-indigo-600 text-white' : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  By {m}
                </button>
              ))
            )}
          </div>

          {/* Style switcher */}
          <div className="flex items-center gap-1 bg-white/10 rounded-full p-0.5 flex-shrink-0">
            <button
              onClick={() => setSlideStyle('basic')}
              title="Basic view"
              className={`px-3 py-1 rounded-full text-xs transition-colors whitespace-nowrap ${
                slideStyle === 'basic' ? 'bg-white text-black font-medium' : 'text-white/70 hover:text-white'
              }`}
            >
              Basic
            </button>
            <button
              onClick={() => setSlideStyle('magazine')}
              title="Magazine view"
              className={`px-3 py-1 rounded-full text-xs transition-colors whitespace-nowrap ${
                slideStyle === 'magazine' ? 'bg-white text-black font-medium' : 'text-white/70 hover:text-white'
              }`}
            >
              ✦ Magazine
            </button>
            <button
              onClick={() => setSlideStyle('photobook')}
              title="Photo Book view"
              className={`px-3 py-1 rounded-full text-xs transition-colors whitespace-nowrap ${
                slideStyle === 'photobook' ? 'bg-white text-black font-medium' : 'text-white/70 hover:text-white'
              }`}
            >
              ❧ Photo Book
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-xs text-stone-400 whitespace-nowrap">
            {slides.length > 0 ? `${index + 1} / ${slides.length}` : ''}
          </span>
          {/* Auto-play toggle */}
          <button
            onClick={() => setAutoPlay(v => !v)}
            title={autoPlay ? 'Pause auto-play' : 'Start auto-play'}
            className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${autoPlay ? 'bg-indigo-600 text-white' : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'}`}
          >
            {autoPlay ? (
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>

          {/* Background music */}
          <div className="relative flex-shrink-0">
            <div className="flex items-center bg-white/10 rounded-full">
              <button
                data-testid="music-toggle"
                onClick={toggleMusicPlayback}
                title={musicPlaying ? 'Pause music' : 'Play music'}
                className={`w-7 h-7 rounded-full flex items-center justify-center text-sm transition-colors ${musicPlaying ? 'text-white' : 'text-white/40'}`}
              >
                🎵
              </button>
              <button
                data-testid="music-menu-toggle"
                onClick={() => setMusicMenuOpen(v => !v)}
                title="Choose music"
                className="w-5 h-7 flex items-center justify-center text-white/50 hover:text-white text-[0.55rem]"
              >
                ▾
              </button>
            </div>
            {musicMenuOpen && (
              <div className="absolute right-0 top-9 w-60 bg-stone-900 border border-white/10 rounded-xl shadow-xl py-1.5 z-20">
                <button
                  data-testid="music-option-none"
                  onClick={() => selectMusic(NO_MUSIC_ID)}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-white/10 transition-colors ${
                    musicTrackId === NO_MUSIC_ID ? 'text-indigo-300' : 'text-white/80'
                  }`}
                >
                  No music {musicTrackId === NO_MUSIC_ID && '✓'}
                </button>
                {MUSIC_TRACKS.map(t => (
                  <button
                    key={t.id}
                    data-testid={`music-option-${t.id}`}
                    onClick={() => selectMusic(t.id)}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-white/10 transition-colors ${
                      activeMusicTrack?.id === t.id ? 'text-indigo-300' : 'text-white/80'
                    }`}
                  >
                    <div className="font-medium">{t.title} {activeMusicTrack?.id === t.id && '✓'}</div>
                    <div className="text-white/40 text-[0.65rem] mt-0.5">{t.composer}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button onClick={() => navigate('/events')} className="text-stone-400 hover:text-white text-2xl leading-none flex-shrink-0">×</button>
        </div>
      </div>

      {/* Auto-play progress bar */}
      {autoPlay && slides.length > 0 && (
        <div className="h-0.5 bg-white/10 flex-shrink-0">
          {waitingForAudio ? (
            // Last photo shown — pulse while the voice story finishes
            <div className="h-full bg-indigo-500/60 animate-pulse w-full" />
          ) : (
            // Countdown to the next photo (or next event)
            <div
              key={progressKey}
              className="h-full bg-indigo-500 origin-left"
              style={{ animation: `carousel-progress ${photoDwellMs(photoCount)}ms linear forwards` }}
            />
          )}
        </div>
      )}

      {/* Main slide */}
      <div className="flex-1 relative overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-stone-500">Loading…</p>
          </div>
        ) : !current ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-stone-500">No events to show.</p>
          </div>
        ) : slideStyle === 'photobook' ? (
          <PhotoBookSlide
            current={current}
            currentPhoto={currentPhoto}
            photoIndex={photoIndex}
            setPhotoIndex={setPhotoIndex}
            index={index}
            slides={slides}
            prev={prev}
            next={next}
            advancePhoto={advancePhoto}
            audioPlaying={audioPlaying}
            setAudioPlaying={setAudioPlaying}
            audioRef={audioRef}
          />
        ) : slideStyle === 'magazine' ? (
          <MagazineSlide
            current={current}
            currentPhoto={currentPhoto}
            photoIndex={photoIndex}
            setPhotoIndex={setPhotoIndex}
            index={index}
            slides={slides}
            prev={retreatPhoto}
            next={advancePhoto}
            advancePhoto={advancePhoto}
            audioPlaying={audioPlaying}
            setAudioPlaying={setAudioPlaying}
            audioRef={audioRef}
          />
        ) : (
          <BasicSlide
            current={current}
            currentPhoto={currentPhoto}
            photoIndex={photoIndex}
            setPhotoIndex={setPhotoIndex}
            index={index}
            slides={slides}
            prev={retreatPhoto}
            next={advancePhoto}
            advancePhoto={advancePhoto}
            audioPlaying={audioPlaying}
            setAudioPlaying={setAudioPlaying}
            audioRef={audioRef}
          />
        )}
      </div>

      {/* Thumbnail strip */}
      {slides.length > 1 && (
        <div className="h-16 bg-black/80 flex items-center gap-1 px-4 overflow-x-auto flex-shrink-0">
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              onClick={() => setIndex(i)}
              className={`flex-shrink-0 w-12 h-12 rounded overflow-hidden border-2 transition-all ${
                i === index ? 'border-indigo-500 scale-110' : 'border-transparent opacity-50 hover:opacity-80'
              }`}
            >
              {slide.photos[0] ? (
                <img src={slide.photos[0].filePath} alt="" loading="lazy" className="w-full h-full object-cover" style={{ objectPosition: '50% 25%' }} />
              ) : (
                <div className="w-full h-full bg-stone-700" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Shared props ─────────────────────────────────────────────────────────────

interface SlideProps {
  current: CarouselEvent;
  currentPhoto: { filePath: string } | null;
  photoIndex: number;
  setPhotoIndex: (fn: (i: number) => number) => void;
  index: number;
  slides: CarouselEvent[];
  prev: () => void;
  next: () => void;
  advancePhoto: () => void;
  audioPlaying: string | null;
  setAudioPlaying: (id: string | null) => void;
  audioRef: (clipId: string) => (el: HTMLAudioElement | null) => void;
}

// ─── Basic view ───────────────────────────────────────────────────────────────

function BasicSlide({ current, currentPhoto, photoIndex, setPhotoIndex, index, slides, prev, next, advancePhoto, audioPlaying, setAudioPlaying, audioRef }: SlideProps) {
  return (
    <div className="w-full h-full flex flex-col landscape:flex-row">
      <div
        className="flex-1 flex items-center justify-center bg-black relative group cursor-pointer select-none min-h-0 min-w-0"
        onClick={advancePhoto}
      >
        {currentPhoto ? (
          <img src={currentPhoto.filePath} alt={current.title} className="max-h-full max-w-full object-contain" />
        ) : (
          <div className="text-stone-600 text-center" onClick={e => e.stopPropagation()}>
            <svg className="w-24 h-24 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p>No photo</p>
          </div>
        )}
        <NavArrows index={index} slides={slides} prev={prev} next={next} />
        {current.photos.length > 0 && (
          <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-60 transition-opacity text-white text-xs pointer-events-none">
            click to advance
          </div>
        )}
      </div>

      <div className="w-full landscape:w-80 shrink-0 landscape:max-h-full max-h-[45vh] bg-stone-900 text-white p-6 flex flex-col overflow-y-auto">
        <h2 className="text-2xl font-serif font-bold mb-2">{current.title}</h2>
        {current.eventDate && (
          <p className="text-indigo-300 text-sm mb-1">{format(parseEventDate(current.eventDate), 'MMMM d, yyyy')}</p>
        )}
        {current.location && <p className="text-stone-400 text-sm mb-3">{current.location}</p>}
        {current.photos.length > 1 && (
          <div className="flex gap-1.5 mb-3">
            {current.photos.map((_, i) => (
              <button
                key={i}
                onClick={() => setPhotoIndex(() => i)}
                className={`w-2 h-2 rounded-full transition-colors ${i === photoIndex ? 'bg-indigo-400' : 'bg-stone-600 hover:bg-stone-400'}`}
              />
            ))}
          </div>
        )}
        {current.description && <p className="text-stone-300 text-sm leading-relaxed mb-4 whitespace-pre-wrap">{current.description}</p>}
        <AudioPanel clips={current.audioClips} audioPlaying={audioPlaying} setAudioPlaying={setAudioPlaying} audioRef={audioRef} />
      </div>
    </div>
  );
}

// ─── Magazine view (Gallery exhibit style) ───────────────────────────────────

function MagazineSlide({ current, currentPhoto, photoIndex, setPhotoIndex, index, slides, prev, next, advancePhoto, setAudioPlaying, audioRef }: SlideProps) {
  const dateStr = current.eventDate ? format(parseEventDate(current.eventDate), 'MMMM yyyy') : null;
  const meta = [dateStr, current.location].filter(Boolean).join(' · ');

  return (
    <div className="w-full h-full relative flex flex-col bg-zinc-950 select-none">
      {/* Photo — gallery print centered with breathing room */}
      <div
        className="flex-1 flex items-center justify-center cursor-pointer px-14 pt-8 pb-3"
        style={{ minHeight: 0 }}
        onClick={advancePhoto}
      >
        {currentPhoto ? (
          <img
            src={currentPhoto.filePath}
            alt={current.title}
            className="max-h-full max-w-full object-contain"
            style={{ boxShadow: '0 30px 80px rgba(0,0,0,0.95), 0 0 0 1px rgba(255,255,255,0.07)' }}
          />
        ) : (
          <div className="flex items-center justify-center w-full h-full">
            <svg className="w-20 h-20 text-zinc-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
      </div>

      {/* Caption — below the photo, never overlaid */}
      <div className="flex-shrink-0 px-14 pb-7 text-center">
        <div className="w-8 h-px bg-white/15 mx-auto mb-3" />
        <h2 className="text-base font-serif font-light tracking-widest text-white/85 mb-1">{current.title}</h2>
        {meta && <p className="text-[0.65rem] text-zinc-500 tracking-[0.2em] uppercase">{meta}</p>}

        {current.photos.length > 1 && (
          <div className="flex justify-center gap-1.5 mt-3" onClick={e => e.stopPropagation()}>
            {current.photos.map((_, i) => (
              <button
                key={i}
                onClick={() => setPhotoIndex(() => i)}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${i === photoIndex ? 'bg-white/60' : 'bg-zinc-700 hover:bg-zinc-500'}`}
              />
            ))}
          </div>
        )}

        {current.audioClips.length > 0 && (
          <div className="mt-4 flex flex-col gap-2 max-w-xs mx-auto text-left" onClick={e => e.stopPropagation()}>
            <p className="text-[0.6rem] text-zinc-600 tracking-[0.2em] uppercase text-center mb-1">Voice Stories</p>
            {current.audioClips.map(clip => (
              <div key={clip.id}>
                {clip.label && <p className="text-[0.65rem] text-zinc-500 mb-0.5">{clip.label}</p>}
                <audio
                  ref={audioRef(clip.id)}
                  src={clip.filePath}
                  controls
                  className="w-full h-7"
                  onPlay={() => setAudioPlaying(clip.id)}
                  onPause={() => setAudioPlaying(null)}
                  onEnded={() => setAudioPlaying(null)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <NavArrows index={index} slides={slides} prev={prev} next={next} />
    </div>
  );
}

// ─── Photo Book view (react-pageflip) ────────────────────────────────────────

type PageFlipHandle = {
  pageFlip: () => {
    flipNext: (corner?: 'top' | 'bottom') => void;
    flipPrev: (corner?: 'top' | 'bottom') => void;
    flip:     (page: number, corner?: 'top' | 'bottom') => void;
  };
};

function PhotoBookSlide({ photoIndex, setPhotoIndex, index, slides, prev, next, setAudioPlaying, audioRef }: SlideProps) {
  const bookRef = useRef<PageFlipHandle>(null);
  const [shownIndex, setShownIndex] = useState(index);
  const shownIndexRef = useRef(index);
  const progFlip = useRef(false);

  // Keyboard / thumbnail nav: parent index changes → drive book programmatically
  useEffect(() => {
    if (index === shownIndex) return;
    progFlip.current = true;
    bookRef.current?.pageFlip().flip(index * 2);
    setShownIndex(index);
    shownIndexRef.current = index;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  // User drags a page → sync parent
  const onFlip = useCallback((e: { data: number }) => {
    const eventIdx = Math.floor(e.data / 2);
    if (!progFlip.current) {
      const diff = eventIdx - shownIndexRef.current;
      if (diff > 0) next(); else if (diff < 0) prev();
      setShownIndex(eventIdx);
      shownIndexRef.current = eventIdx;
    }
    progFlip.current = false;
  }, [next, prev]);

  const flipPrev = () => bookRef.current?.pageFlip().flipPrev('bottom');
  const flipNext = () => bookRef.current?.pageFlip().flipNext('bottom');

  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center select-none overflow-hidden relative"
      style={{ background: 'radial-gradient(ellipse at 50% 45%, #2a1206 0%, #0a0401 100%)' }}
    >
      {/* Ambient candlelight */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 55% 32% at 50% 50%, rgba(255,215,140,0.08) 0%, transparent 70%)',
      }} />

      {/* Book — react-pageflip handles all 3D physics, curl shadows, drag */}
      <div
        className="relative"
        style={{
          width:  'min(94vw, 980px)',
          height: 'min(76vh, 560px)',
          filter: 'drop-shadow(0 45px 70px rgba(0,0,0,0.9)) drop-shadow(0 8px 18px rgba(0,0,0,0.7))',
        }}
      >
        <HTMLFlipBook
          ref={bookRef as any}
          width={460} height={560}
          size="stretch"
          minWidth={160} maxWidth={460}
          minHeight={280} maxHeight={560}
          drawShadow={true}
          flippingTime={680}
          usePortrait={true}
          startZIndex={1}
          autoSize={false}
          maxShadowOpacity={0.5}
          showCover={false}
          mobileScrollSupport={true}
          clickEventForward={true}
          useMouseEvents={true}
          swipeDistance={25}
          showPageCorners={true}
          disableFlipByClick={false}
          startPage={index * 2}
          className=""
          style={{}}
          onFlip={onFlip}
        >
          {slides.flatMap((slide, i) => {
            const photo = (i === shownIndex ? slide.photos[photoIndex] : undefined) ?? slide.photos[0] ?? null;
            const dateStr = slide.eventDate ? format(parseEventDate(slide.eventDate), 'MMMM d, yyyy') : null;
            return [
              /* ── LEFT page: info ── */
              <div key={`L-${slide.id}`} className="overflow-hidden relative flex flex-col h-full"
                style={{ background: 'linear-gradient(112deg, #fef9f1 0%, #f3e8d4 100%)' }}>
                {/* Ruled lines */}
                <div className="absolute inset-0 pointer-events-none" style={{
                  backgroundImage: 'repeating-linear-gradient(transparent, transparent 27px, rgba(145,105,60,0.1) 27px, rgba(145,105,60,0.1) 28px)',
                  backgroundPosition: '0 54px',
                }} />
                {/* Red margin */}
                <div className="absolute top-0 bottom-0 w-px pointer-events-none" style={{ left: 40, background: 'rgba(210,110,90,0.14)' }} />

                <div className="relative z-10 flex flex-col h-full p-5 pl-8">
                  <div className="flex items-center gap-2 mb-3 mt-1">
                    <div className="h-px w-10" style={{ background: 'rgba(100,65,30,0.24)' }} />
                    <div className="w-1 h-1 rounded-full" style={{ background: 'rgba(100,65,30,0.3)' }} />
                    <div className="h-px w-4" style={{ background: 'rgba(100,65,30,0.18)' }} />
                  </div>

                  <h2 className="font-serif text-xl leading-snug mb-1" style={{ color: '#2e1c0c' }}>{slide.title}</h2>
                  {dateStr && <p className="text-sm font-light mb-0.5" style={{ color: '#7a5230' }}>{dateStr}</p>}
                  {slide.location && <p className="text-xs italic mb-3" style={{ color: '#a07850' }}>{slide.location}</p>}

                  {slide.description ? (
                    <p className="text-sm leading-relaxed font-light flex-1 overflow-hidden whitespace-pre-wrap" style={{
                      color: '#4a3018',
                      display: '-webkit-box', WebkitLineClamp: 11, WebkitBoxOrient: 'vertical',
                    } as React.CSSProperties}>
                      {slide.description}
                    </p>
                  ) : <div className="flex-1" />}

                  {slide.photos.length > 1 && i === shownIndex && (
                    <div className="flex gap-2 my-2">
                      {slide.photos.map((_, pi) => (
                        <button key={pi} onClick={() => setPhotoIndex(() => pi)}
                          className="w-2 h-2 rounded-full transition-all"
                          style={{ background: pi === photoIndex ? 'rgba(80,50,20,0.72)' : 'rgba(80,50,20,0.2)' }}
                        />
                      ))}
                    </div>
                  )}

                  {slide.audioClips.length > 0 && (
                    <div className="mt-auto pt-3" style={{ borderTop: '1px solid rgba(120,85,45,0.13)' }}>
                      <p className="text-[0.55rem] tracking-[0.22em] uppercase mb-1.5" style={{ color: '#a07850' }}>Voice Stories</p>
                      {slide.audioClips.slice(0, 1).map(clip => (
                        <div key={clip.id}>
                          {clip.label && <p className="text-[0.6rem] mb-0.5" style={{ color: '#7a5230' }}>{clip.label}</p>}
                          <audio ref={audioRef(clip.id)} src={clip.filePath} controls className="w-full h-7"
                            onPlay={() => setAudioPlaying(clip.id)} onPause={() => setAudioPlaying(null)} onEnded={() => setAudioPlaying(null)} />
                        </div>
                      ))}
                    </div>
                  )}

                  <p className="text-center text-[0.65rem] font-serif italic mt-3" style={{ color: 'rgba(100,65,30,0.3)' }}>— {i * 2 + 1} —</p>
                </div>
              </div>,

              /* ── RIGHT page: photo ── */
              <div key={`R-${slide.id}`} className="overflow-hidden relative flex flex-col h-full"
                style={{ background: 'linear-gradient(248deg, #fef9f1 0%, #f3e8d4 100%)' }}>

                <div className="flex-1 flex items-center justify-center p-5" style={{ minHeight: 0 }}>
                  {photo ? (
                    <div className="relative inline-flex">
                      <img src={photo.filePath} alt={slide.title} style={{
                        maxHeight: 'calc(min(76vh, 560px) - 72px)',
                        maxWidth: '100%',
                        objectFit: 'contain',
                        transform: 'rotate(-0.4deg)',
                        borderRadius: 2,
                        boxShadow: '4px 6px 24px rgba(0,0,0,0.28), 0 1px 4px rgba(0,0,0,0.12)',
                      }} />
                      {/* Album mount corners */}
                      <div className="absolute top-1.5 left-1.5  w-4 h-4 border-t-2 border-l-2 pointer-events-none" style={{ borderColor: 'rgba(75,50,20,0.34)' }} />
                      <div className="absolute top-1.5 right-1.5 w-4 h-4 border-t-2 border-r-2 pointer-events-none" style={{ borderColor: 'rgba(75,50,20,0.34)' }} />
                      <div className="absolute bottom-1.5 left-1.5  w-4 h-4 border-b-2 border-l-2 pointer-events-none" style={{ borderColor: 'rgba(75,50,20,0.34)' }} />
                      <div className="absolute bottom-1.5 right-1.5 w-4 h-4 border-b-2 border-r-2 pointer-events-none" style={{ borderColor: 'rgba(75,50,20,0.34)' }} />
                    </div>
                  ) : (
                    <div className="w-36 h-36 flex items-center justify-center rounded" style={{ border: '1px dashed rgba(120,85,45,0.22)' }}>
                      <svg className="w-12 h-12" style={{ color: 'rgba(120,85,45,0.26)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                </div>

                <div className="flex-shrink-0 pb-3 px-4 text-center">
                  <div className="w-5 h-px mx-auto mb-1.5" style={{ background: 'rgba(100,65,30,0.17)' }} />
                  <p className="text-[0.65rem] font-serif italic truncate" style={{ color: 'rgba(75,48,18,0.44)' }}>{slide.title}</p>
                  <p className="text-[0.65rem] font-serif italic mt-0.5" style={{ color: 'rgba(100,65,30,0.26)' }}>— {i * 2 + 2} —</p>
                </div>

                {/* Corner curl (CSS triangle — react-pageflip also animates its own curl on hover) */}
                <div className="absolute bottom-0 right-0 w-10 h-10 pointer-events-none" style={{
                  background: 'linear-gradient(225deg, #ddd0ba 45%, transparent 45%)',
                  boxShadow: '-2px -2px 6px rgba(0,0,0,0.09)',
                }} />
              </div>,
            ];
          })}
        </HTMLFlipBook>
      </div>

      {/* Nav bar below the book */}
      <div className="flex items-center gap-6 mt-4 z-10">
        <button onClick={flipPrev} disabled={slides.length <= 1}
          className="flex items-center gap-1.5 transition-opacity disabled:opacity-20"
          style={{ color: 'rgba(215,168,95,0.75)' }}>
          <span className="text-2xl leading-none">‹</span>
          <span className="text-[0.58rem] tracking-widest uppercase">Prev</span>
        </button>

        <span className="text-xs font-serif italic tracking-widest" style={{ color: 'rgba(185,148,88,0.42)' }}>
          {shownIndex + 1} / {slides.length}
        </span>

        <button onClick={flipNext} disabled={slides.length <= 1}
          className="flex items-center gap-1.5 transition-opacity disabled:opacity-20"
          style={{ color: 'rgba(215,168,95,0.75)' }}>
          <span className="text-[0.58rem] tracking-widest uppercase">Next</span>
          <span className="text-2xl leading-none">›</span>
        </button>
      </div>

      <p className="text-[0.5rem] tracking-[0.22em] uppercase mt-1.5 hidden sm:block"
        style={{ color: 'rgba(180,140,80,0.25)' }}>
        Drag corners · swipe · or use arrows to turn pages
      </p>
    </div>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function NavArrows({ slides, prev, next }: { index: number; slides: CarouselEvent[]; prev: () => void; next: () => void }) {
  if (slides.length <= 1) return null;
  return (
    <>
      <button
        onClick={e => { e.stopPropagation(); prev(); }}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl transition-colors z-10"
      >
        ‹
      </button>
      <button
        onClick={e => { e.stopPropagation(); next(); }}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/80 text-white rounded-full w-10 h-10 flex items-center justify-center text-xl transition-colors z-10"
      >
        ›
      </button>
    </>
  );
}


function AudioPanel({ clips, audioPlaying, setAudioPlaying, audioRef }: {
  clips: CarouselEvent['audioClips'];
  audioPlaying: string | null;
  setAudioPlaying: (id: string | null) => void;
  audioRef: (clipId: string) => (el: HTMLAudioElement | null) => void;
}) {
  if (!clips.length) return null;
  return (
    <div className="mt-auto">
      <p className="text-xs text-stone-500 uppercase tracking-wide mb-2">Voice Stories</p>
      {clips.map(clip => (
        <div key={clip.id} className="mb-3">
          {clip.label && <p className="text-xs text-indigo-300 mb-1">{clip.label}</p>}
          <audio
            ref={audioRef(clip.id)}
            src={clip.filePath}
            controls
            className="w-full h-8"
            onPlay={() => setAudioPlaying(clip.id)}
            onPause={() => setAudioPlaying(null)}
            onEnded={() => setAudioPlaying(null)}
          />
          {audioPlaying === clip.id && (
            <div className="flex gap-1 mt-1">
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  className="w-1 bg-indigo-500 rounded-full animate-pulse"
                  style={{ height: `${8 + Math.random() * 16}px`, animationDelay: `${i * 0.1}s` }}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
